// src/features/license/IssueLicenseDialog.tsx
// Master admin: issue a new signed license for a customer platform.

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useLicense, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type { License, LicenseEntitlements, LicenseType, ValidationMode } from '@core/types/license';
import { EntitlementsEditor } from './EntitlementsEditor';
import { errMessage, makeEntity } from './format';

const LICENSE_TYPES: LicenseType[] = ['LICENSE_TYPE_PERPETUAL', 'LICENSE_TYPE_SUBSCRIPTION', 'LICENSE_TYPE_TRIAL', 'LICENSE_TYPE_EVALUATION', 'LICENSE_TYPE_DEVELOPMENT', 'LICENSE_TYPE_OEM'];
const VALIDATION_MODES: ValidationMode[] = ['VALIDATION_MODE_OFFLINE', 'VALIDATION_MODE_ONLINE', 'VALIDATION_MODE_HYBRID'];

interface IssueLicenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (license: License) => void;
}

function toInputDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

export const IssueLicenseDialog = observer(({ open, onClose, onSaved }: IssueLicenseDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLicense();
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerTenantId, setCustomerTenantId] = useState('');
  const [targetPlatformId, setTargetPlatformId] = useState('');
  const [licenseType, setLicenseType] = useState<LicenseType>('LICENSE_TYPE_SUBSCRIPTION');
  const [validationMode, setValidationMode] = useState<ValidationMode>('VALIDATION_MODE_HYBRID');
  const [isTrial, setIsTrial] = useState(false);
  const [maxUsers, setMaxUsers] = useState('0');
  const [maxTenants, setMaxTenants] = useState('0');
  const [maxNodes, setMaxNodes] = useState('0');
  const [validFrom, setValidFrom] = useState(toInputDate(new Date()));
  const [validUntil, setValidUntil] = useState(toInputDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)));
  const [gracePeriodDays, setGracePeriodDays] = useState('14');
  const [heartbeatHours, setHeartbeatHours] = useState('24');
  const [entitlements, setEntitlements] = useState<LicenseEntitlements>({ products: [], features: [] });
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName('');
    setDescription('');
    setCustomerTenantId('');
    setTargetPlatformId('');
    setLicenseType('LICENSE_TYPE_SUBSCRIPTION');
    setValidationMode('VALIDATION_MODE_HYBRID');
    setIsTrial(false);
    setMaxUsers('0');
    setMaxTenants('0');
    setMaxNodes('0');
    setValidFrom(toInputDate(new Date()));
    setValidUntil(toInputDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)));
    setGracePeriodDays('14');
    setHeartbeatHours('24');
    setEntitlements({ products: [], features: [] });
    setMoreFields(false);
  }, [open]);

  const dirty = !!(name.trim() || customerTenantId.trim() || targetPlatformId.trim());

  const save = async () => {
    if (!name.trim() || !customerTenantId.trim() || !targetPlatformId.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const saved = await store.IssueLicense({
        entity: makeEntity('', 'license', scope),
        name: name.trim(),
        description,
        customer: makeEntity(customerTenantId.trim(), 'tenant', scope),
        target_platform: makeEntity(targetPlatformId.trim(), 'platform', scope),
        subscription: undefined,
        license_type: licenseType,
        validation_mode: validationMode,
        is_trial: isTrial,
        entitlements,
        max_users: Number(maxUsers) || 0,
        max_tenants: Number(maxTenants) || 0,
        max_storage_bytes: 0,
        max_nodes: Number(maxNodes) || 0,
        valid_from: new Date(validFrom).toISOString(),
        valid_until: new Date(validUntil).toISOString(),
        grace_period_days: Number(gracePeriodDays) || 0,
        heartbeat_interval_hours: Number(heartbeatHours) || 0,
        max_offline_days: 0,
        metadata: {},
      });
      onSaved(saved.license!);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('license.issue.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={save}
      submitLabel={t('license.issue.action')}
      canSubmit={!!name.trim() && !!customerTenantId.trim() && !!targetPlatformId.trim()}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={t('license.issue.title')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={`${t('common.name')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('common.name')}
        />
        <AutoGrowTextarea ref={descRef} value={description} onChange={setDescription} placeholder={`${t('common.description')}…`} minRows={1} />
      </div>

      <div className="space-y-1 px-3.5 pb-2">
        <FormRow label={t('license.issue.customerTenantId')} labelWidth="sm">
          <input
            value={customerTenantId}
            onChange={(e) => setCustomerTenantId(e.target.value)}
            aria-label={t('license.issue.customerTenantId')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('license.issue.targetPlatformId')} labelWidth="sm">
          <input
            value={targetPlatformId}
            onChange={(e) => setTargetPlatformId(e.target.value)}
            aria-label={t('license.issue.targetPlatformId')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <div className="w-44">
          <Select value={licenseType} onChange={(e) => setLicenseType(e.target.value as LicenseType)} options={LICENSE_TYPES.map((v) => ({ value: v, label: t(`license.type.${v.replace('LICENSE_TYPE_', '').toLowerCase()}`) }))} aria-label={t('license.field.type')} fullWidth />
        </div>
        <SegmentedControl
          size="sm"
          value={validationMode}
          onChange={(v) => setValidationMode(v as ValidationMode)}
          options={VALIDATION_MODES.map((v) => ({ value: v, label: t(`license.validationMode.${v.replace('VALIDATION_MODE_', '').toLowerCase()}`) }))}
          aria-label={t('license.field.validationMode')}
        />
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('license.field.isTrial')}
        </label>
      </div>

      {moreFields && (
        <div className="space-y-1 border-t border-border px-3.5 py-2">
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormRow label={t('license.field.validFrom')} labelWidth="sm" htmlFor="license-valid-from">
              <input id="license-valid-from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('license.field.validUntil')} labelWidth="sm" htmlFor="license-valid-until">
              <input id="license-valid-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { label: t('license.field.maxUsers'), value: maxUsers, set: setMaxUsers },
              { label: t('license.field.maxTenants'), value: maxTenants, set: setMaxTenants },
              { label: t('license.field.maxNodes'), value: maxNodes, set: setMaxNodes },
              { label: t('license.field.gracePeriodDays'), value: gracePeriodDays, set: setGracePeriodDays },
              { label: t('license.field.heartbeatHours'), value: heartbeatHours, set: setHeartbeatHours },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
                <span className="text-xs text-muted-foreground">{f.label}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={f.value}
                  onChange={(e) => f.set(digits(e.target.value))}
                  aria-label={f.label}
                  className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
                />
              </span>
            ))}
          </div>
          <div className="pt-2">
            <EntitlementsEditor value={entitlements} onChange={setEntitlements} />
          </div>
        </div>
      )}
    </FormDialog>
  );
});
